import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { api, apiUtils } from '../services/api';

const STATUS_OPTIONS = [
  { value: 'development', label: 'En Desarrollo' },
  { value: 'funding', label: 'En Financiamiento' },
  { value: 'construction', label: 'En Construcción' },
  { value: 'operational', label: 'Operativo' },
  { value: 'completed', label: 'Completado' },
];

const CALCULATION_METHOD_OPTIONS = [
  { value: 'injection', label: 'Método Inyección' },
  { value: 'self_consumption_injection', label: 'Método Autoconsumo + Inyección' },
];

const SALE_PRICING_MODE_OPTIONS = [
  { value: 'fixed', label: 'Precio fijo' },
  { value: 'promotional', label: 'Rangos' },
  { value: 'curve', label: 'Curva' },
];

const PUBLIC_PROJECT_URL_BASE = 'https://www.comunidadsolar.com.ar/comunidades-solares/';

const CURVE_DEFAULTS = {
  curve_coefficient_a: '0.0000002',
  curve_coefficient_b: '-0.0004',
  curve_coefficient_c: '0.7986',
};

const CURVE_COEFFICIENT_FIELDS = [
  { name: 'curve_coefficient_a', label: 'a - coeficiente cuadrático' },
  { name: 'curve_coefficient_b', label: 'b - coeficiente lineal' },
];

const resourceConfigs = {
  projects: {
    title: 'Proyectos solares',
    resource: 'projects',
    label: (item) => item.name || `Proyecto #${item.id}`,
    fields: [
      { name: 'name', label: 'Nombre', required: true },
      { name: 'slug', label: 'URL', type: 'projectUrl', wide: true },
      { name: 'description', label: 'Descripción', type: 'textarea', required: true },
      { name: 'location', label: 'Ubicación', required: true },
      { name: 'status', label: 'Estado', type: 'select', options: STATUS_OPTIONS },
      { name: 'total_power_installed', label: 'Potencia instalada (kWp)', type: 'number', localizedNumber: true, required: true },
      { name: 'total_power_projected', label: 'Potencia proyectada (kWp)', type: 'number', localizedNumber: true, required: true },
      { name: 'available_power', label: 'Potencia disponible (kWp)', type: 'number', localizedNumber: true, readOnly: true },
      { name: 'panel_power_wp', label: 'Potencia por panel (Wp)', type: 'number', localizedNumber: true, required: true },
      { name: 'owners', label: 'Propietarios', type: 'textarea', required: true },
      { name: 'expected_annual_generation', label: 'Generación anual esperada (MWh/año)', type: 'number', localizedNumber: true, mwhInputForKwhField: true, nullable: true, wide: true },
      { name: 'om_percentage', label: 'O&M (%)', type: 'number', localizedNumber: true, required: true, defaultValue: '5', suffix: '%', helpText: 'Porcentaje de energía que retiene el proyecto. Se descuenta de la energía acreditada en cada simulación.' },
      { name: 'commercial_whatsapp', label: 'WhatsApp comercial' },
      { name: 'funding_goal', label: 'Meta de financiamiento (USD)', type: 'number', localizedNumber: true, nullable: true, showWhen: (form) => form?.status === 'funding' },
      { name: 'funding_raised', label: 'Financiamiento recaudado (USD)', type: 'number', localizedNumber: true, showWhen: (form) => form?.status === 'funding' },
      { name: 'funding_deadline', label: 'Fecha límite', type: 'date', nullable: true, showWhen: (form) => form?.status === 'funding' },
    ],
  },
  'exchange-rates': {
    title: 'Tipos de cambio',
    resource: 'exchange-rates',
    label: (item) => `${item.rate} ARS/USD - ${item.date}`,
    fields: [
      { name: 'rate', label: 'ARS por USD', type: 'number', required: true },
      { name: 'source', label: 'Fuente', required: true },
      { name: 'date', label: 'Fecha', type: 'date', required: true },
    ],
  },
  'site-settings': {
    title: 'Configuración del sitio',
    singleton: true,
    label: () => 'Configuración general',
    fields: [
      { name: 'site_name', label: 'Nombre del sitio', required: true },
      { name: 'site_description', label: 'Descripción', type: 'textarea' },
      { name: 'contact_email', label: 'Email de contacto', type: 'email' },
      { name: 'contact_phone', label: 'Teléfono de contacto' },
      { name: 'address', label: 'Dirección', type: 'textarea' },
      { name: 'facebook_url', label: 'Facebook', type: 'url' },
      { name: 'twitter_url', label: 'Twitter/X', type: 'url' },
      { name: 'linkedin_url', label: 'LinkedIn', type: 'url' },
      { name: 'instagram_url', label: 'Instagram', type: 'url' },
      { name: 'meta_keywords', label: 'Palabras clave SEO', type: 'textarea' },
      { name: 'google_analytics_id', label: 'Google Analytics ID' },
      { name: 'default_annual_generation_factor', label: 'Factor generación anual', type: 'number' },
      { name: 'default_performance_ratio', label: 'Performance ratio', type: 'number' },
    ],
  },
  'contact-messages': {
    title: 'Mensajes de contacto',
    resource: 'contact-messages',
    readOnlyCreate: true,
    label: (item) => `${item.subject} - ${item.email}`,
    fields: [
      { name: 'name', label: 'Nombre', readOnly: true },
      { name: 'email', label: 'Email', readOnly: true },
      { name: 'phone', label: 'Teléfono', readOnly: true },
      { name: 'subject', label: 'Asunto', readOnly: true },
      { name: 'message', label: 'Mensaje', type: 'textarea', readOnly: true },
      { name: 'is_read', label: 'Leído', type: 'checkbox' },
    ],
  },
  newsletter: {
    title: 'Newsletter',
    resource: 'newsletter',
    label: (item) => item.email,
    fields: [
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'name', label: 'Nombre' },
      { name: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
  },
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normalizeLocalizedNumber = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';

  if (stringValue.includes(',')) {
    const normalized = stringValue
      .replace(/\./g, '')
      .replace(',', '.')
      .replace(/[^\d.]/g, '');
    const [integerPart, ...decimalParts] = normalized.split('.');
    const decimalPart = decimalParts.join('');
    return decimalParts.length > 0 ? `${integerPart}.${decimalPart}` : integerPart;
  }

  const normalized = stringValue.replace(/[^\d.]/g, '');
  if (/^\d{1,3}(\.\d{3})+$/.test(normalized)) {
    return normalized.replace(/\./g, '');
  }

  const [integerPart, ...decimalParts] = normalized.split('.');
  const decimalPart = decimalParts.join('');
  return decimalParts.length > 0 ? `${integerPart}.${decimalPart}` : integerPart;
};

const normalizeCurveCoefficientInput = (value) => {
  if (value === null || value === undefined) return '';
  const rawValue = String(value).trim().replace(',', '.');
  const isNegative = rawValue.startsWith('-');
  const unsignedValue = rawValue.replace(/-/g, '').replace(/[^\d.]/g, '');
  const [integerPart, ...decimalParts] = unsignedValue.split('.');
  const decimalPart = decimalParts.join('');
  const normalized = decimalParts.length > 0 ? `${integerPart}.${decimalPart}` : integerPart;
  return `${isNegative ? '-' : ''}${normalized}`;
};

const normalizeProjectSlugInput = (value) => {
  const rawValue = String(value || '').trim();
  const editablePart = rawValue.includes('/comunidades-solares/')
    ? rawValue.split('/comunidades-solares/').pop()
    : rawValue;

  return editablePart
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9/-]+/g, '-')
    .replace(/\//g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const formatLocalizedNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const [integerPart, decimalPart] = String(value).replace(',', '.').split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimalPart !== undefined ? `${formattedInteger},${decimalPart}` : formattedInteger;
};

const normalizeArsCurrencyInput = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '');
};

const formatArsCurrencyInput = (value) => {
  const digits = normalizeArsCurrencyInput(value);
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const formatMwhInputValue = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  const hasDecimalComma = stringValue.includes(',');
  const [integerPart, ...decimalParts] = stringValue.split(',');
  const integerDigits = integerPart.replace(/\D/g, '');
  const decimalDigits = decimalParts.join('').replace(/\D/g, '');
  const formattedInteger = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return hasDecimalComma ? `${formattedInteger},${decimalDigits}` : formattedInteger;
};

const decimalToNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const formatDecimalValue = (value, maxDecimals = 2) => {
  if (!Number.isFinite(value)) return '';
  return Number(value.toFixed(maxDecimals)).toString();
};

const TARIFF_DECIMAL_FIELDS = [
  'injection_price_ars_per_kwh',
  'self_consumption_percentage',
  'self_consumption_price_ars_per_kwh',
  'resto_price_without_taxes_ars_per_kwh',
  'tax_sum_percentage',
];

const applyDependentTariffPrices = (tariffPrice, changedField) => {
  const resto = decimalToNumber(tariffPrice.resto_price_without_taxes_ars_per_kwh);
  const tax = decimalToNumber(tariffPrice.tax_sum_percentage);
  const autoconsumo = decimalToNumber(tariffPrice.self_consumption_price_ars_per_kwh);
  const taxFactor = tax === null ? null : 1 + tax / 100;

  if (
    (changedField === 'resto_price_without_taxes_ars_per_kwh' || changedField === 'tax_sum_percentage')
    && resto !== null
    && taxFactor !== null
  ) {
    return {
      ...tariffPrice,
      self_consumption_price_ars_per_kwh: formatDecimalValue(resto * taxFactor),
    };
  }

  if (
    changedField === 'self_consumption_price_ars_per_kwh'
    && autoconsumo !== null
    && taxFactor
  ) {
    return {
      ...tariffPrice,
      resto_price_without_taxes_ars_per_kwh: formatDecimalValue(autoconsumo / taxFactor),
    };
  }

  return tariffPrice;
};

const syncProjectPanelPricing = (current, changedFieldName, normalizedValue) => {
  const next = { ...current, [changedFieldName]: normalizedValue };
  const pricePerWp = decimalToNumber(next.price_per_wp_usd);
  const pricePerPanel = decimalToNumber(next.price_per_panel_usd);
  const panelPowerWp = decimalToNumber(next.panel_power_wp);

  if (changedFieldName === 'price_per_wp_usd') {
    next.price_per_panel_usd =
      pricePerWp !== null && panelPowerWp !== null && panelPowerWp > 0
        ? formatDecimalValue(pricePerWp * panelPowerWp)
        : '';
  }

  if (changedFieldName === 'price_per_panel_usd') {
    next.price_per_wp_usd =
      pricePerPanel !== null && panelPowerWp !== null && panelPowerWp > 0
        ? formatDecimalValue(pricePerPanel / panelPowerWp)
        : '';
  }

  if (changedFieldName === 'panel_power_wp' && panelPowerWp !== null && panelPowerWp > 0) {
    if (pricePerWp !== null) {
      next.price_per_panel_usd = formatDecimalValue(pricePerWp * panelPowerWp);
    } else if (pricePerPanel !== null) {
      next.price_per_wp_usd = formatDecimalValue(pricePerPanel / panelPowerWp);
    }
  }

  next.curve_coefficient_c = normalizeCurveCoefficientInput(next.price_per_wp_usd);

  return next;
};

const withCurveDefaults = (project) => {
  if (!project) return project;
  return {
    ...project,
    curve_coefficient_a: project.curve_coefficient_a ?? CURVE_DEFAULTS.curve_coefficient_a,
    curve_coefficient_b: project.curve_coefficient_b ?? CURVE_DEFAULTS.curve_coefficient_b,
    curve_coefficient_c: project.price_per_wp_usd ?? project.curve_coefficient_c ?? CURVE_DEFAULTS.curve_coefficient_c,
  };
};

const getCurveCoefficientValue = (form, fieldName) =>
  normalizeCurveCoefficientInput(
    fieldName === 'curve_coefficient_c'
      ? form?.price_per_wp_usd ?? form?.curve_coefficient_c ?? CURVE_DEFAULTS[fieldName]
      : form?.[fieldName] ?? CURVE_DEFAULTS[fieldName]
  );

const kwhToMwhValue = (value) => {
  const numberValue = decimalToNumber(value);
  if (numberValue === null) return '';
  return String(numberValue / 1000);
};

const mwhToKwhValue = (value) => {
  const normalizedValue = normalizeLocalizedNumber(value);
  const numberValue = decimalToNumber(normalizedValue);
  if (numberValue === null) return '';
  return String(numberValue * 1000);
};

const emptyFormFor = (fields) =>
  fields.reduce((acc, field) => {
    acc[field.name] = field.defaultValue ?? (
      field.type === 'checkbox'
        ? false
        : field.type === 'select'
          ? field.options?.[0]?.value || ''
          : ''
    );
    return acc;
  }, {});

const cleanPayload = (form, fields) =>
  fields.reduce((acc, field) => {
    if (field.readOnly) return acc;
    const value = form[field.name];
    if (value === '') {
      if (field.nullable) {
        acc[field.name] = null;
      } else if (field.required) {
        acc[field.name] = value;
      }
      return acc;
    }
    acc[field.name] = value;
    return acc;
  }, {});

const emptyTariffPriceForm = (project) => ({
  name: '',
  code: '',
  calculation_method: 'injection',
  injection_price_ars_per_kwh: project?.energy_price_ars_per_kwh || '',
  self_consumption_percentage: '',
  resto_price_without_taxes_ars_per_kwh: '',
  tax_sum_percentage: '',
  self_consumption_price_ars_per_kwh: '',
  max_purchase_panels: '',
});

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayInputValue = () => toDateInputValue(new Date());

const minSaleDateInputValue = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return toDateInputValue(date);
};

const emptyParticipationSaleForm = () => ({
  name: '',
  is_public: true,
  power_sold_kwp: '',
  amount_ars: '',
  sale_date: todayInputValue(),
  logo_file: null,
});

const emptyPromotionalRangeForm = () => ({
  max_panels: '',
  discount_percentage: '',
});

const cleanParticipationSalePayload = (form) => ({
  name: (form.name || '').trim(),
  is_public: form.is_public !== false,
  power_sold_kwp: normalizeLocalizedNumber(form.power_sold_kwp),
  amount_ars: form.amount_ars === '' || form.amount_ars == null
    ? null
    : normalizeArsCurrencyInput(form.amount_ars),
  sale_date: form.sale_date || todayInputValue(),
});

const cleanPromotionalRangePayload = (form, minPanels, isBaseRange) => ({
  min_panels: minPanels,
  max_panels: form.max_panels === '' ? null : parseInt(form.max_panels, 10),
  discount_percentage: isBaseRange ? '0' : normalizeLocalizedNumber(form.discount_percentage),
});

const cleanTariffPricePayload = (form) => {
  const payload = {
    ...form,
    code: (form.code || '').toUpperCase().trim(),
    injection_price_ars_per_kwh: normalizeLocalizedNumber(form.injection_price_ars_per_kwh),
    self_consumption_percentage:
      form.self_consumption_percentage === ''
        ? null
        : normalizeLocalizedNumber(form.self_consumption_percentage),
    community_energy_percentage: null,
    self_consumption_price_ars_per_kwh:
      form.self_consumption_price_ars_per_kwh === ''
        ? null
        : normalizeLocalizedNumber(form.self_consumption_price_ars_per_kwh),
    resto_price_without_taxes_ars_per_kwh:
      form.resto_price_without_taxes_ars_per_kwh === ''
        ? null
        : normalizeLocalizedNumber(form.resto_price_without_taxes_ars_per_kwh),
    tax_sum_percentage:
      form.tax_sum_percentage === ''
        ? null
        : normalizeLocalizedNumber(form.tax_sum_percentage),
    max_purchase_panels:
      form.max_purchase_panels === '' || form.max_purchase_panels == null
        ? null
        : parseInt(form.max_purchase_panels, 10),
  };

  if (payload.calculation_method === 'injection') {
    payload.self_consumption_percentage = null;
    payload.self_consumption_price_ars_per_kwh = null;
    payload.resto_price_without_taxes_ars_per_kwh = null;
    payload.tax_sum_percentage = null;
  }

  return payload;
};

const AdministrationPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem('adminToken') || '');
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [activeKey, setActiveKey] = useState('projects');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [salePricingSaving, setSalePricingSaving] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [imageSaving, setImageSaving] = useState(false);
  const [videoSaving, setVideoSaving] = useState(false);
  const [tariffPrices, setTariffPrices] = useState([]);
  const [tariffForm, setTariffForm] = useState(emptyTariffPriceForm());
  const [tariffLoading, setTariffLoading] = useState(false);
  const [tariffSaving, setTariffSaving] = useState(false);
  const [participationSales, setParticipationSales] = useState([]);
  const [participationSaleForm, setParticipationSaleForm] = useState(emptyParticipationSaleForm());
  const [participationSalesLoading, setParticipationSalesLoading] = useState(false);
  const [participationSalesSaving, setParticipationSalesSaving] = useState(false);
  const [promotionalRanges, setPromotionalRanges] = useState([]);
  const [promotionalRangeForm, setPromotionalRangeForm] = useState(emptyPromotionalRangeForm());
  const [promotionalRangesLoading, setPromotionalRangesLoading] = useState(false);
  const [promotionalRangesSaving, setPromotionalRangesSaving] = useState(false);
  const [mwhInputDrafts, setMwhInputDrafts] = useState({});

  const config = useMemo(() => resourceConfigs[activeKey], [activeKey]);

  const loadResource = React.useCallback(async () => {
    if (!token || !config) return;
    setLoading(true);
    try {
      if (config.singleton) {
        const data = await api.adminGetSiteSettings();
        setItems([data]);
        setSelected(data);
        setForm(data);
      } else {
        const data = await api.adminList(config.resource);
        const list = normalizeList(data);
        setItems(list);
        if (list.length > 0) {
          setSelected(list[0]);
          setForm(config.resource === 'projects' ? withCurveDefaults(list[0]) : list[0]);
        } else {
          setSelected(null);
          setForm(emptyFormFor(config.fields));
        }
      }
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, config]);

  const loadProjectTariffPrices = React.useCallback(async (projectId) => {
    setTariffLoading(true);
    try {
      const data = await api.adminListProjectTariffPrices(projectId);
      setTariffPrices(normalizeList(data));
      setTariffForm(emptyTariffPriceForm(selected));
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setTariffLoading(false);
    }
  }, [selected]);

  const loadProjectParticipationSales = React.useCallback(async (projectId) => {
    setParticipationSalesLoading(true);
    try {
      const data = await api.adminListProjectParticipationSales(projectId);
      setParticipationSales(normalizeList(data));
      setParticipationSaleForm(emptyParticipationSaleForm());
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setParticipationSalesLoading(false);
    }
  }, []);

  const loadProjectPromotionalRanges = React.useCallback(async (projectId) => {
    setPromotionalRangesLoading(true);
    try {
      const data = await api.adminListProjectPromotionalPriceRanges(projectId);
      setPromotionalRanges(normalizeList(data));
      setPromotionalRangeForm(emptyPromotionalRangeForm());
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setPromotionalRangesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      api.adminMe()
        .then(setUser)
        .catch(() => handleLogout());
    }
  }, [token]);

  useEffect(() => {
    loadResource();
  }, [loadResource]);

  useEffect(() => {
    if (activeKey !== 'projects' || !selected?.id || !token) {
      setTariffPrices([]);
      setTariffForm(emptyTariffPriceForm());
      setParticipationSales([]);
      setParticipationSaleForm(emptyParticipationSaleForm());
      setPromotionalRanges([]);
      setPromotionalRangeForm(emptyPromotionalRangeForm());
      return;
    }

    loadProjectTariffPrices(selected.id);
    loadProjectParticipationSales(selected.id);
    loadProjectPromotionalRanges(selected.id);
  }, [activeKey, selected?.id, token, loadProjectTariffPrices, loadProjectParticipationSales, loadProjectPromotionalRanges]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken('');
    setUser(null);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await api.adminLogin(loginForm);
      localStorage.setItem('adminToken', data.token);
      setToken(data.token);
      setUser(data.user);
      toast.success('Sesión iniciada');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    setSelected(item);
    setForm(activeKey === 'projects' ? withCurveDefaults(item) : item);
    setMwhInputDrafts({});
  };

  const handleNew = () => {
    setSelected(null);
    setForm(emptyFormFor(config.fields));
    setMwhInputDrafts({});
  };

  const handleFieldChange = (field, value) => {
    if (field.type === 'projectUrl') {
      setForm((current) => ({ ...current, [field.name]: normalizeProjectSlugInput(value) }));
      return;
    }

    if (field.mwhInputForKwhField) {
      const displayValue = formatMwhInputValue(value);
      setMwhInputDrafts((current) => ({ ...current, [field.name]: displayValue }));
      setForm((current) => ({ ...current, [field.name]: mwhToKwhValue(displayValue) }));
      return;
    }

    if (field.localizedNumber) {
      const normalizedValue = normalizeLocalizedNumber(value);
      if (['price_per_wp_usd', 'price_per_panel_usd', 'panel_power_wp'].includes(field.name)) {
        setForm((current) => syncProjectPanelPricing(current, field.name, normalizedValue));
        return;
      }

      setForm((current) => ({ ...current, [field.name]: normalizedValue }));
      return;
    }

    setForm((current) => ({ ...current, [field.name]: value }));
  };

  const handleMwhFieldBlur = (field) => {
    setMwhInputDrafts((current) => {
      const next = { ...current };
      delete next[field.name];
      return next;
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = cleanPayload(form, config.fields);
      if (config.resource === 'projects') {
        if (!selected?.id) {
          payload.price_per_wp_usd = form.price_per_wp_usd || '0';
          payload.price_per_panel_usd = form.price_per_panel_usd || null;
          payload.sale_pricing_mode = form.sale_pricing_mode || 'fixed';
          payload.curve_coefficient_a = getCurveCoefficientValue(form, 'curve_coefficient_a');
          payload.curve_coefficient_b = getCurveCoefficientValue(form, 'curve_coefficient_b');
          payload.curve_coefficient_c = normalizeLocalizedNumber(form.price_per_wp_usd || form.curve_coefficient_c || CURVE_DEFAULTS.curve_coefficient_c);
        }
        if (payload.status !== 'funding') {
          payload.funding_goal = null;
          payload.funding_raised = '0';
          payload.funding_deadline = null;
        }
      }
      if (config.singleton) {
        const data = await api.adminUpdateSiteSettings(payload);
        setSelected(data);
        setForm(data);
        toast.success('Configuración actualizada');
      } else if (selected?.id) {
        const data = await api.adminUpdate(config.resource, selected.id, payload);
        setSelected(data);
        setForm(config.resource === 'projects' ? withCurveDefaults(data) : data);
        toast.success('Registro actualizado');
      } else {
        const data = await api.adminCreate(config.resource, payload);
        setSelected(data);
        setForm(config.resource === 'projects' ? withCurveDefaults(data) : data);
        toast.success('Registro creado');
      }
      await loadResource();
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected?.id || config.singleton) return;
    if (!window.confirm('¿Eliminar este registro? Esta acción no se puede deshacer.')) return;
    setSaving(true);
    try {
      await api.adminDelete(config.resource, selected.id);
      toast.success('Registro eliminado');
      await loadResource();
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const refreshProjects = async (projectId = selected?.id, { preserveForm = false } = {}) => {
    const data = await api.adminList('projects');
    const list = normalizeList(data);
    const current = list.find((item) => item.id === projectId) || list[0] || null;
    setItems(list);
    setSelected(current);
    if (!preserveForm) {
      setForm(current ? withCurveDefaults(current) : emptyFormFor(config.fields));
    }
    return current;
  };

  const shouldFallbackToBackendUpload = (error) => {
    const status = error?.response?.status;
    const message = apiUtils.extractErrorMessage(error).toLowerCase();
    return status === 404 || (status === 400 && message.includes('r2'));
  };

  const handleLogoUpload = async (event, type) => {
    const file = event.target.files?.[0];
    if (!selected?.id || !file) return;

    setLogoSaving(true);
    try {
      let data;
      try {
        data = await api.adminPrepareProjectLogoUpload(selected.id, type, file);
        await api.uploadFileToPresignedUrl(data.presigned_url, file, data.content_type);
      } catch (presignedError) {
        if (!shouldFallbackToBackendUpload(presignedError)) throw presignedError;
        data = await api.adminUploadProjectLogo(selected.id, type, file);
      }
      setSelected(data);
      setItems((current) => current.map((item) => (item.id === data.id ? data : item)));
      toast.success(type === 'banner' ? 'Banner actualizado' : 'Logo actualizado');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      event.target.value = '';
      setLogoSaving(false);
    }
  };

  const handleLogoDelete = async (type) => {
    if (!selected?.id) return;
    setLogoSaving(true);
    try {
      const data = await api.adminDeleteProjectLogo(selected.id, type);
      setSelected(data);
      setItems((current) => current.map((item) => (item.id === data.id ? data : item)));
      toast.success(type === 'banner' ? 'Banner eliminado' : 'Logo eliminado');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setLogoSaving(false);
    }
  };

  const handleImageSubmit = async (event) => {
    event.preventDefault();
    if (!selected?.id) return;

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const imageFile = formData.get('image');
    if (!imageFile?.name) {
      toast.error('Seleccioná una imagen');
      return;
    }

    setImageSaving(true);
    try {
      let createdImageId = null;
      try {
        const prepared = await api.adminPrepareProjectImageUpload(selected.id, imageFile, {
          caption: formData.get('caption') || '',
          is_featured: formData.get('is_featured') === 'true',
          order: formData.get('order') || 0,
        });
        createdImageId = prepared.image?.id;
        await api.uploadFileToPresignedUrl(prepared.presigned_url, imageFile, prepared.content_type);
      } catch (presignedError) {
        if (createdImageId) {
          await api.adminDeleteProjectImage(selected.id, createdImageId).catch(() => {});
        }
        if (!shouldFallbackToBackendUpload(presignedError)) throw presignedError;
        await api.adminCreateProjectImage(selected.id, formData);
      }
      await refreshProjects(selected.id, { preserveForm: true });
      formElement.reset();
      toast.success('Imagen agregada');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setImageSaving(false);
    }
  };

  const handleVideoSubmit = async (event) => {
    event.preventDefault();
    if (!selected?.id) return;

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const videoFile = formData.get('video');
    const videoUrl = formData.get('video_url');
    if (!videoFile?.name && !videoUrl) {
      toast.error('Subí un video o cargá una URL');
      return;
    }

    setVideoSaving(true);
    try {
      let createdVideoId = null;
      if (videoFile?.name) {
        try {
          const prepared = await api.adminPrepareProjectVideoUpload(selected.id, videoFile, {
            title: formData.get('title') || videoFile.name,
            description: formData.get('description') || '',
            video_url: videoUrl || '',
            order: formData.get('order') || 0,
          });
          createdVideoId = prepared.video?.id;
          await api.uploadFileToPresignedUrl(prepared.presigned_url, videoFile, prepared.content_type);
        } catch (presignedError) {
          if (createdVideoId) {
            await api.adminDeleteProjectVideo(selected.id, createdVideoId).catch(() => {});
          }
          if (!shouldFallbackToBackendUpload(presignedError)) throw presignedError;
          await api.adminCreateProjectVideo(selected.id, formData);
        }
      } else {
        await api.adminCreateProjectVideo(selected.id, formData);
      }
      await refreshProjects(selected.id, { preserveForm: true });
      formElement.reset();
      toast.success('Video agregado');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setVideoSaving(false);
    }
  };

  const handleMediaDelete = async (kind, mediaId) => {
    if (!selected?.id || !window.confirm('¿Eliminar este archivo multimedia?')) return;

    const setSavingForKind = kind === 'image' ? setImageSaving : setVideoSaving;
    setSavingForKind(true);
    try {
      if (kind === 'image') {
        await api.adminDeleteProjectImage(selected.id, mediaId);
      } else {
        await api.adminDeleteProjectVideo(selected.id, mediaId);
      }
      await refreshProjects(selected.id, { preserveForm: true });
      toast.success('Multimedia eliminada');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setSavingForKind(false);
    }
  };

  const handleTariffPriceChange = (tariffId, field, value) => {
    const normalizedValue = TARIFF_DECIMAL_FIELDS.includes(field)
      ? normalizeLocalizedNumber(value)
      : field === 'max_purchase_panels'
        ? String(value || '').replace(/\D/g, '')
      : value;
    setTariffPrices((current) =>
      current.map((item) => (
        item.id === tariffId
          ? applyDependentTariffPrices({ ...item, [field]: normalizedValue }, field)
          : item
      ))
    );
  };

  const handleTariffFormChange = (field, value) => {
    const normalizedValue = TARIFF_DECIMAL_FIELDS.includes(field)
      ? normalizeLocalizedNumber(value)
      : field === 'max_purchase_panels'
        ? String(value || '').replace(/\D/g, '')
      : value;
    setTariffForm((current) => applyDependentTariffPrices({ ...current, [field]: normalizedValue }, field));
  };

  const handleParticipationSaleFormChange = (field, value) => {
    const normalizedValue = field === 'logo_file'
      ? value
      : field === 'power_sold_kwp'
      ? normalizeLocalizedNumber(value)
      : field === 'amount_ars'
        ? normalizeArsCurrencyInput(value)
        : value;
    setParticipationSaleForm((current) => ({ ...current, [field]: normalizedValue }));
  };

  const handleParticipationSaleLocalChange = (saleId, field, value) => {
    const normalizedValue = field === 'amount_ars'
      ? normalizeArsCurrencyInput(value)
      : value;
    setParticipationSales((current) =>
      current.map((sale) => (sale.id === saleId ? { ...sale, [field]: normalizedValue } : sale))
    );
  };

  const uploadParticipationSaleLogo = async (saleId, file) => {
    if (!selected?.id || !saleId || !file) return null;
    return api.adminUploadProjectParticipationSaleLogo(selected.id, saleId, file);
  };

  const handleParticipationSaleUpdate = async (sale, changes) => {
    if (!selected?.id || !sale?.id) return;

    setParticipationSalesSaving(true);
    try {
      await api.adminUpdateProjectParticipationSale(selected.id, sale.id, {
        ...cleanParticipationSalePayload(sale),
        ...changes,
      });
      await loadProjectParticipationSales(selected.id);
      await refreshProjects(selected.id, { preserveForm: true });
      toast.success('Venta actualizada');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setParticipationSalesSaving(false);
    }
  };

  const handleParticipationSaleCreate = async (event) => {
    event?.preventDefault();
    if (!selected?.id) return;
    if (!participationSaleForm.name.trim()) {
      toast.error('Ingresá el nombre de la venta');
      return;
    }
    const normalizedPower = normalizeLocalizedNumber(participationSaleForm.power_sold_kwp);
    if (!normalizedPower || Number(normalizedPower) <= 0) {
      toast.error('Ingresá una potencia vendida mayor a 0');
      return;
    }

    setParticipationSalesSaving(true);
    try {
      const createdSale = await api.adminCreateProjectParticipationSale(
        selected.id,
        cleanParticipationSalePayload(participationSaleForm)
      );
      if (participationSaleForm.logo_file) {
        await uploadParticipationSaleLogo(createdSale.id, participationSaleForm.logo_file);
      }
      toast.success('Venta registrada');
      await loadProjectParticipationSales(selected.id);
      await refreshProjects(selected.id, { preserveForm: true });
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setParticipationSalesSaving(false);
    }
  };

  const handleParticipationSaleLogoUpload = async (event, sale) => {
    const file = event.target.files?.[0];
    if (!selected?.id || !sale?.id || !file) return;

    setParticipationSalesSaving(true);
    try {
      await uploadParticipationSaleLogo(sale.id, file);
      toast.success('Logo actualizado');
      await loadProjectParticipationSales(selected.id);
      await refreshProjects(selected.id, { preserveForm: true });
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      event.target.value = '';
      setParticipationSalesSaving(false);
    }
  };

  const handleParticipationSaleLogoDelete = async (sale) => {
    if (!selected?.id || !sale?.id) return;

    setParticipationSalesSaving(true);
    try {
      await api.adminDeleteProjectParticipationSaleLogo(selected.id, sale.id);
      toast.success('Logo eliminado');
      await loadProjectParticipationSales(selected.id);
      await refreshProjects(selected.id, { preserveForm: true });
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setParticipationSalesSaving(false);
    }
  };

  const handleParticipationSaleDelete = async (saleId) => {
    if (!selected?.id || !window.confirm('¿Eliminar esta venta registrada?')) return;

    setParticipationSalesSaving(true);
    try {
      await api.adminDeleteProjectParticipationSale(selected.id, saleId);
      toast.success('Venta eliminada');
      await loadProjectParticipationSales(selected.id);
      await refreshProjects(selected.id, { preserveForm: true });
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setParticipationSalesSaving(false);
    }
  };

  const buildSalePricingPayload = (nextForm) => ({
    sale_pricing_mode: nextForm.sale_pricing_mode || 'fixed',
    price_per_wp_usd: nextForm.price_per_wp_usd || '0',
    price_per_panel_usd: nextForm.price_per_panel_usd || null,
    curve_coefficient_a: getCurveCoefficientValue(nextForm, 'curve_coefficient_a'),
    curve_coefficient_b: getCurveCoefficientValue(nextForm, 'curve_coefficient_b'),
    curve_coefficient_c: normalizeLocalizedNumber(nextForm.price_per_wp_usd || nextForm.curve_coefficient_c || CURVE_DEFAULTS.curve_coefficient_c),
  });

  const getCurveValidationPanelCounts = (nextForm) => {
    const panelPowerWp = Number(nextForm.panel_power_wp || selected?.panel_power_wp || 0);
    const availablePowerKw = Number(selected?.available_power || nextForm.available_power || 0);
    const panelCounts = new Set([1]);

    if (panelPowerWp > 0 && availablePowerKw > 0) {
      const maxPanels = Math.floor(availablePowerKw / (panelPowerWp / 1000));
      if (maxPanels > 1) {
        panelCounts.add(maxPanels);
        panelCounts.add(Math.max(1, Math.floor(maxPanels / 4)));
        panelCounts.add(Math.max(1, Math.floor(maxPanels / 2)));
        panelCounts.add(Math.max(1, Math.floor((maxPanels * 3) / 4)));
      }
    }

    return Array.from(panelCounts).sort((a, b) => a - b);
  };

  const validateCurvePricing = (nextForm) => {
    if ((nextForm.sale_pricing_mode || 'fixed') !== 'curve') return true;

    const a = Number(getCurveCoefficientValue(nextForm, 'curve_coefficient_a'));
    const b = Number(getCurveCoefficientValue(nextForm, 'curve_coefficient_b'));
    const c = Number(getCurveCoefficientValue(nextForm, 'curve_coefficient_c'));
    if (![a, b, c].every(Number.isFinite)) {
      window.alert('Ingresá valores numéricos válidos para los coeficientes a, b y c.');
      return false;
    }

    const hasInvalidPrice = getCurveValidationPanelCounts(nextForm).some((panels) => (
      (a * panels * panels) + (b * panels) + c <= 0
    ));
    if (hasInvalidPrice) {
      window.alert('Los parámetros configurados generan precios negativos o nulos. Ajustá a, b o c antes de guardar.');
      return false;
    }

    return true;
  };

  const handleSalePricingModeChange = async (mode) => {
    if (!selected?.id) return;
    const nextForm = withCurveDefaults({ ...form, sale_pricing_mode: mode });
    if (!validateCurvePricing(nextForm)) return;

    setForm(nextForm);
    setSalePricingSaving(true);
    try {
      const data = await api.adminUpdate('projects', selected.id, buildSalePricingPayload(nextForm));
      setSelected(data);
      setForm((current) => ({
        ...current,
        sale_pricing_mode: data.sale_pricing_mode,
        price_per_wp_usd: data.price_per_wp_usd,
        price_per_panel_usd: data.price_per_panel_usd,
        curve_coefficient_a: data.curve_coefficient_a ?? CURVE_DEFAULTS.curve_coefficient_a,
        curve_coefficient_b: data.curve_coefficient_b ?? CURVE_DEFAULTS.curve_coefficient_b,
        curve_coefficient_c: data.price_per_wp_usd ?? data.curve_coefficient_c ?? CURVE_DEFAULTS.curve_coefficient_c,
      }));
      setItems((current) => current.map((item) => (item.id === data.id ? data : item)));
      toast.success('Modalidad de precios actualizada');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setSalePricingSaving(false);
    }
  };

  const handleSalePricingSave = async () => {
    if (!selected?.id) return;
    const nextForm = withCurveDefaults(form);
    if (!validateCurvePricing(nextForm)) return;

    setSalePricingSaving(true);
    try {
      const data = await api.adminUpdate('projects', selected.id, buildSalePricingPayload(nextForm));
      setSelected(data);
      setForm((current) => ({
        ...current,
        sale_pricing_mode: data.sale_pricing_mode,
        price_per_wp_usd: data.price_per_wp_usd,
        price_per_panel_usd: data.price_per_panel_usd,
        curve_coefficient_a: data.curve_coefficient_a ?? CURVE_DEFAULTS.curve_coefficient_a,
        curve_coefficient_b: data.curve_coefficient_b ?? CURVE_DEFAULTS.curve_coefficient_b,
        curve_coefficient_c: data.price_per_wp_usd ?? data.curve_coefficient_c ?? CURVE_DEFAULTS.curve_coefficient_c,
      }));
      setItems((current) => current.map((item) => (item.id === data.id ? data : item)));
      await loadProjectPromotionalRanges(selected.id);
      toast.success('Precios de venta actualizados');
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setSalePricingSaving(false);
    }
  };

  const getNextPromotionalRangeStart = () => {
    if (promotionalRanges.length === 0) return 1;
    const orderedRanges = [...promotionalRanges].sort((a, b) => Number(a.min_panels) - Number(b.min_panels));
    const lastRange = orderedRanges[orderedRanges.length - 1];
    if (!lastRange.max_panels) return null;
    return Number(lastRange.max_panels) + 1;
  };

  const handlePromotionalRangeCreate = async () => {
    if (!selected?.id) return;
    const minPanels = getNextPromotionalRangeStart();
    if (!minPanels) {
      toast.error('El último rango está abierto. Eliminá o cerrá ese rango para agregar otro.');
      return;
    }

    const isBaseRange = promotionalRanges.length === 0;
    if (!isBaseRange && (!promotionalRangeForm.discount_percentage || Number(normalizeLocalizedNumber(promotionalRangeForm.discount_percentage)) <= 0)) {
      toast.error('Ingresá un porcentaje de descuento mayor a 0');
      return;
    }

    setPromotionalRangesSaving(true);
    try {
      await api.adminCreateProjectPromotionalPriceRange(
        selected.id,
        cleanPromotionalRangePayload(promotionalRangeForm, minPanels, isBaseRange)
      );
      toast.success(isBaseRange ? 'Rango base creado' : 'Rango promocional creado');
      await loadProjectPromotionalRanges(selected.id);
      await refreshProjects(selected.id, { preserveForm: true });
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setPromotionalRangesSaving(false);
    }
  };

  const handlePromotionalRangeDelete = async (rangeId) => {
    if (!selected?.id || !window.confirm('¿Eliminar este rango promocional?')) return;

    setPromotionalRangesSaving(true);
    try {
      await api.adminDeleteProjectPromotionalPriceRange(selected.id, rangeId);
      toast.success('Rango eliminado');
      await loadProjectPromotionalRanges(selected.id);
      await refreshProjects(selected.id, { preserveForm: true });
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setPromotionalRangesSaving(false);
    }
  };

  const handleTariffPriceCreate = async (event) => {
    event.preventDefault();
    if (!selected?.id) return;

    setTariffSaving(true);
    try {
      await api.adminCreateProjectTariffPrice(selected.id, cleanTariffPricePayload(tariffForm));
      toast.success('Categoría tarifaria creada');
      await loadProjectTariffPrices(selected.id);
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setTariffSaving(false);
    }
  };

  const handleTariffPriceSave = async (tariffPrice) => {
    if (!selected?.id || !tariffPrice?.id) return;

    setTariffSaving(true);
    try {
      await api.adminUpdateProjectTariffPrice(
        selected.id,
        tariffPrice.id,
        cleanTariffPricePayload(tariffPrice)
      );
      toast.success('Categoría tarifaria actualizada');
      await loadProjectTariffPrices(selected.id);
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setTariffSaving(false);
    }
  };

  const handleTariffPriceDelete = async (tariffPriceId) => {
    if (!selected?.id || !window.confirm('¿Eliminar esta categoría tarifaria?')) return;

    setTariffSaving(true);
    try {
      await api.adminDeleteProjectTariffPrice(selected.id, tariffPriceId);
      toast.success('Categoría tarifaria eliminada');
      await loadProjectTariffPrices(selected.id);
    } catch (error) {
      toast.error(apiUtils.extractErrorMessage(error));
    } finally {
      setTariffSaving(false);
    }
  };

  const renderParticipationSalesManager = () => {
    if (activeKey !== 'projects' || !selected?.id) return null;

    return (
      <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Participaciones vendidas</h3>
          <p className="text-sm text-gray-600">
            Registrá las ventas del proyecto. La potencia disponible se calcula automáticamente como potencia instalada menos potencia vendida acumulada.
          </p>
        </div>

        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl bg-white p-3 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Potencia instalada</p>
            <p className="text-lg font-semibold text-gray-900">{formatLocalizedNumber(selected.total_power_installed)} kWp</p>
          </div>
          <div className="rounded-xl bg-white p-3 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Potencia vendida acumulada</p>
            <p className="text-lg font-semibold text-primary-700">{formatLocalizedNumber(selected.total_power_sold_kwp || 0)} kWp</p>
          </div>
          <div className="rounded-xl bg-white p-3 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Potencia disponible</p>
            <p className="text-lg font-semibold text-green-700">{formatLocalizedNumber(selected.available_power)} kWp</p>
          </div>
        </div>

        <div
          className="grid gap-3 rounded-xl bg-white p-4 md:grid-cols-7"
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              value={participationSaleForm.name}
              required
              onChange={(event) => handleParticipationSaleFormChange('name', event.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Potencia vendida (kWp)</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              type="text"
              inputMode="decimal"
              value={formatLocalizedNumber(participationSaleForm.power_sold_kwp)}
              required
              onChange={(event) => handleParticipationSaleFormChange('power_sold_kwp', event.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Monto (ARS)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 pl-8 pr-12 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                type="text"
                inputMode="numeric"
                value={formatArsCurrencyInput(participationSaleForm.amount_ars)}
                placeholder="50.000"
                onChange={(event) => handleParticipationSaleFormChange('amount_ars', event.target.value)}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">ARS</span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Fecha</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              type="date"
              value={participationSaleForm.sale_date}
              min={minSaleDateInputValue()}
              max={todayInputValue()}
              required
              onChange={(event) => handleParticipationSaleFormChange('sale_date', event.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Privacidad</label>
            <button
              type="button"
              onClick={() => handleParticipationSaleFormChange('is_public', !participationSaleForm.is_public)}
              className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                participationSaleForm.is_public
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-gray-200 bg-gray-100 text-gray-700'
              }`}
            >
              <span>{participationSaleForm.is_public ? 'Público' : 'Privado'}</span>
              <span className={`h-5 w-9 rounded-full p-0.5 transition-colors ${
                participationSaleForm.is_public ? 'bg-green-500' : 'bg-gray-400'
              }`}>
                <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${
                  participationSaleForm.is_public ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </span>
            </button>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Logo (opcional)</label>
            <input
              type="file"
              accept="image/*"
              className="block w-full text-xs text-gray-700"
              onChange={(event) => handleParticipationSaleFormChange('logo_file', event.target.files?.[0] || null)}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleParticipationSaleCreate}
              disabled={participationSalesSaving}
              className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {participationSalesSaving ? 'Registrando...' : 'Agregar venta'}
            </button>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {participationSalesLoading ? (
            <p className="p-4 text-sm text-gray-500">Cargando ventas...</p>
          ) : participationSales.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">Todavía no hay ventas registradas.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Nombre</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Potencia vendida</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Monto (ARS)</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Fecha</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Privacidad</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Logo</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {participationSales.map((sale) => (
                    <tr key={sale.id}>
                      <td className="px-4 py-2 text-gray-900">{sale.name}</td>
                      <td className="px-4 py-2 text-gray-700">{formatLocalizedNumber(sale.power_sold_kwp)} kWp</td>
                      <td className="px-4 py-2 text-gray-700">
                        <div className="relative w-40">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                          <input
                            className="w-full rounded-lg border border-gray-300 py-1 pl-6 pr-10 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                            type="text"
                            inputMode="numeric"
                            value={formatArsCurrencyInput(sale.amount_ars)}
                            placeholder="50.000"
                            disabled={participationSalesSaving}
                            onChange={(event) => handleParticipationSaleLocalChange(sale.id, 'amount_ars', event.target.value)}
                            onBlur={(event) => handleParticipationSaleUpdate(sale, {
                              amount_ars: event.target.value === '' || event.target.value == null
                                ? null
                                : normalizeArsCurrencyInput(event.target.value),
                            })}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">ARS</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-700">{sale.sale_date}</td>
                      <td className="px-4 py-2 text-gray-700">
                        <button
                          type="button"
                          onClick={() => handleParticipationSaleUpdate(sale, { is_public: !sale.is_public })}
                          disabled={participationSalesSaving}
                          className={`flex w-28 items-center justify-between rounded-full px-2 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${
                            sale.is_public
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          <span>{sale.is_public ? 'Público' : 'Privado'}</span>
                          <span className={`h-4 w-7 rounded-full p-0.5 ${
                            sale.is_public ? 'bg-green-500' : 'bg-gray-400'
                          }`}>
                            <span className={`block h-3 w-3 rounded-full bg-white transition-transform ${
                              sale.is_public ? 'translate-x-3' : 'translate-x-0'
                            }`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        <div className="flex min-w-44 items-center gap-3">
                          {sale.logo_url ? (
                            <img src={sale.logo_url} alt={`Logo ${sale.name}`} className="h-8 w-8 rounded object-contain" />
                          ) : (
                            <span className="text-xs text-gray-400">Sin logo</span>
                          )}
                          <div className="flex flex-col gap-1">
                            <input
                              type="file"
                              accept="image/*"
                              disabled={participationSalesSaving}
                              onChange={(event) => handleParticipationSaleLogoUpload(event, sale)}
                              className="max-w-36 text-xs"
                            />
                            {sale.logo_url && (
                              <button
                                type="button"
                                disabled={participationSalesSaving}
                                onClick={() => handleParticipationSaleLogoDelete(sale)}
                                className="text-left text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                              >
                                Eliminar logo
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleParticipationSaleDelete(sale.id)}
                          disabled={participationSalesSaving}
                          className="text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProjectSalePricingManager = () => {
    if (activeKey !== 'projects' || !selected?.id) return null;

    const pricingMode = form.sale_pricing_mode || 'fixed';
    const nextRangeStart = getNextPromotionalRangeStart();
    const isBaseRange = promotionalRanges.length === 0;
    const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100';
    const curveFormula = `y = ${getCurveCoefficientValue(form, 'curve_coefficient_a')}x² + ${getCurveCoefficientValue(form, 'curve_coefficient_b')}x + ${getCurveCoefficientValue(form, 'curve_coefficient_c')}`;
    const isCurveMode = pricingMode === 'curve';
    const handleCurveCoefficientChange = (fieldName, value) => {
      setForm((current) => ({
        ...current,
        [fieldName]: normalizeCurveCoefficientInput(value),
      }));
    };

    return (
      <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Precios de Venta del Proyecto</h3>
          <p className="text-sm text-gray-600">
            Definí si el proyecto usa un precio fijo, rangos promocionales o una curva por cantidad de paneles. El precio por Wp y por panel se mantienen sincronizados como precio base.
          </p>
        </div>

        <div className="mb-4 inline-flex rounded-xl border border-gray-200 bg-white p-1">
          {SALE_PRICING_MODE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSalePricingModeChange(option.value)}
              disabled={salePricingSaving}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                pricingMode === option.value
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="grid gap-4 rounded-xl bg-white p-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {isCurveMode ? 'Precio Base por Wp (USD)' : 'Precio por Wp (USD)'}
            </label>
            <input
              className={inputClass}
              type="text"
              inputMode="decimal"
              value={formatLocalizedNumber(form.price_per_wp_usd)}
              onChange={(event) => handleFieldChange(
                { name: 'price_per_wp_usd', localizedNumber: true },
                event.target.value
              )}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {isCurveMode ? 'Precio Base por panel (USD)' : 'Precio por panel (USD)'}
            </label>
            <input
              className={inputClass}
              type="text"
              inputMode="decimal"
              value={formatLocalizedNumber(form.price_per_panel_usd)}
              onChange={(event) => handleFieldChange(
                { name: 'price_per_panel_usd', localizedNumber: true },
                event.target.value
              )}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleSalePricingSave}
              disabled={salePricingSaving}
              className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {salePricingSaving ? 'Guardando...' : 'Guardar precios'}
            </button>
          </div>
        </div>

        {pricingMode === 'curve' && (
          <div className="mt-4 space-y-4 rounded-xl bg-white p-4">
            <div>
              <h4 className="font-semibold text-gray-900">Curva de precio por cantidad de paneles</h4>
              <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900">{curveFormula}</p>
              <p className="mt-2 text-sm text-gray-600">
                x = cantidad de paneles. y = precio unitario calculado en USD/Wp. R² = 1 no forma parte del cálculo.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              {CURVE_COEFFICIENT_FIELDS.map((field) => (
                <div key={field.name}>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{field.label}</label>
                  <input
                    className={inputClass}
                    type="text"
                    inputMode="decimal"
                    value={getCurveCoefficientValue(form, field.name)}
                    placeholder={CURVE_DEFAULTS[field.name]}
                    onChange={(event) => handleCurveCoefficientChange(field.name, event.target.value)}
                  />
                </div>
              ))}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  c - Precio Base por Wp (USD)
                </label>
                <input
                  className={`${inputClass} bg-gray-50 text-gray-600`}
                  type="text"
                  value={formatLocalizedNumber(getCurveCoefficientValue(form, 'curve_coefficient_c'))}
                  readOnly
                />
              </div>
            </div>
          </div>
        )}

        {pricingMode === 'promotional' && (
          <div className="mt-4 space-y-4 rounded-xl bg-white p-4">
            <div>
              <h4 className="font-semibold text-gray-900">Rangos</h4>
              <p className="text-sm text-gray-600">
                El primer rango usa el precio base. Los rangos siguientes calculan el precio aplicando el descuento sobre ese precio base.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Desde paneles</label>
                <input className={`${inputClass} bg-gray-50 text-gray-600`} value={nextRangeStart || 'Rango abierto'} readOnly />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Hasta paneles</label>
                <input
                  className={inputClass}
                  type="number"
                  min={nextRangeStart || 1}
                  step="1"
                  placeholder="Dejar vacío para abierto"
                  value={promotionalRangeForm.max_panels}
                  disabled={!nextRangeStart}
                  onChange={(event) => setPromotionalRangeForm((current) => ({
                    ...current,
                    max_panels: event.target.value.replace(/\D/g, ''),
                  }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {isBaseRange ? 'Descuento' : 'Descuento sobre precio base (%)'}
                </label>
                <input
                  className={`${inputClass} ${isBaseRange ? 'bg-gray-50 text-gray-600' : ''}`}
                  type="text"
                  inputMode="decimal"
                  value={isBaseRange ? '0' : formatLocalizedNumber(promotionalRangeForm.discount_percentage)}
                  disabled={isBaseRange || !nextRangeStart}
                  onChange={(event) => setPromotionalRangeForm((current) => ({
                    ...current,
                    discount_percentage: normalizeLocalizedNumber(event.target.value),
                  }))}
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handlePromotionalRangeCreate}
                  disabled={promotionalRangesSaving || !nextRangeStart}
                  className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  {isBaseRange ? 'Agregar rango base' : 'Agregar rango'}
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200">
              {promotionalRangesLoading ? (
                <p className="p-4 text-sm text-gray-500">Cargando rangos...</p>
              ) : promotionalRanges.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">Todavía no hay rangos promocionales.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">Rango</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">Descuento</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">USD/Wp</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">USD/panel</th>
                        <th className="px-4 py-2 text-right font-semibold text-gray-600">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {promotionalRanges.map((range) => (
                        <tr key={range.id}>
                          <td className="px-4 py-2 text-gray-900">
                            {range.min_panels}-{range.max_panels || '+'} paneles
                          </td>
                          <td className="px-4 py-2 text-gray-700">{formatLocalizedNumber(range.discount_percentage)}%</td>
                          <td className="px-4 py-2 text-gray-700">${formatLocalizedNumber(range.price_per_wp_usd)}</td>
                          <td className="px-4 py-2 text-gray-700">${formatLocalizedNumber(range.price_per_panel_usd)}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handlePromotionalRangeDelete(range.id)}
                              disabled={promotionalRangesSaving}
                              className="text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTariffPriceFields = (tariffPrice, onChange, fieldPrefix = 'tariff') => {
    const isSelfConsumption = tariffPrice.calculation_method === 'self_consumption_injection';
    const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100';
    const fieldName = (name) => `${fieldPrefix}-${tariffPrice.id || 'new'}-${name}`;

    return (
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
          <input
            id={fieldName('name')}
            name={fieldName('name')}
            className={inputClass}
            value={tariffPrice.name || ''}
            required
            autoComplete="off"
            onChange={(event) => onChange('name', event.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Código</label>
          <input
            id={fieldName('code')}
            name={fieldName('code')}
            className={inputClass}
            value={tariffPrice.code || ''}
            required
            autoComplete="off"
            onChange={(event) => onChange('code', event.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Método de cálculo rentabilidad</label>
          <select
            id={fieldName('calculation_method')}
            name={fieldName('calculation_method')}
            className={inputClass}
            value={tariffPrice.calculation_method || 'injection'}
            onChange={(event) => onChange('calculation_method', event.target.value)}
          >
            {CALCULATION_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Precio Tarifa Inyección (ARS/kWh)</label>
          <input
            key={fieldName('injection_price_ars_per_kwh')}
            id={fieldName('injection_price_ars_per_kwh')}
            name={fieldName('injection_price_ars_per_kwh')}
            className={inputClass}
            type="text"
            inputMode="decimal"
            value={formatLocalizedNumber(tariffPrice.injection_price_ars_per_kwh)}
            required
            autoComplete="off"
            onChange={(event) => onChange('injection_price_ars_per_kwh', event.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Límite Máximo de Compra (Cantidad de paneles)
          </label>
          <input
            id={fieldName('max_purchase_panels')}
            name={fieldName('max_purchase_panels')}
            className={inputClass}
            type="number"
            min="0"
            step="1"
            value={tariffPrice.max_purchase_panels || ''}
            placeholder="Sin límite"
            autoComplete="off"
            onChange={(event) => onChange('max_purchase_panels', event.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">Dejalo vacío o en 0 para no aplicar límite.</p>
        </div>
        {isSelfConsumption && (
          <>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">% Autoconsumo (demanda energía Resto)</label>
              <div className="flex overflow-hidden rounded-lg border border-gray-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
                <input
                  id={fieldName('self_consumption_percentage')}
                  name={fieldName('self_consumption_percentage')}
                  className="w-full px-3 py-2 text-sm focus:outline-none"
                  type="text"
                  inputMode="decimal"
                  value={formatLocalizedNumber(tariffPrice.self_consumption_percentage)}
                  required
                  autoComplete="off"
                  onChange={(event) => onChange('self_consumption_percentage', event.target.value)}
                />
                <span className="flex items-center bg-gray-50 px-3 text-sm font-medium text-gray-600">%</span>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Suma de impuestos <span className="font-normal text-gray-500">NO incluir IVA</span>
              </label>
              <div className="flex overflow-hidden rounded-lg border border-gray-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
                <input
                  id={fieldName('tax_sum_percentage')}
                  name={fieldName('tax_sum_percentage')}
                  className="w-full px-3 py-2 text-sm focus:outline-none"
                  type="text"
                  inputMode="decimal"
                  value={formatLocalizedNumber(tariffPrice.tax_sum_percentage)}
                  autoComplete="off"
                  onChange={(event) => onChange('tax_sum_percentage', event.target.value)}
                />
                <span className="flex items-center bg-gray-50 px-3 text-sm font-medium text-gray-600">%</span>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Precio Tarifa Resto sin impuestos</label>
              <input
                key={fieldName('resto_price_without_taxes_ars_per_kwh')}
                id={fieldName('resto_price_without_taxes_ars_per_kwh')}
                name={fieldName('resto_price_without_taxes_ars_per_kwh')}
                className={inputClass}
                type="text"
                inputMode="decimal"
                value={formatLocalizedNumber(tariffPrice.resto_price_without_taxes_ars_per_kwh)}
                autoComplete="off"
                onChange={(event) => onChange('resto_price_without_taxes_ars_per_kwh', event.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Precio Tarifa Autoconsumo (ARS/kWh)</label>
              <input
                key={fieldName('self_consumption_price_ars_per_kwh')}
                id={fieldName('self_consumption_price_ars_per_kwh')}
                name={fieldName('self_consumption_price_ars_per_kwh')}
                className={inputClass}
                type="text"
                inputMode="decimal"
                value={formatLocalizedNumber(tariffPrice.self_consumption_price_ars_per_kwh)}
                required
                autoComplete="off"
                onChange={(event) => onChange('self_consumption_price_ars_per_kwh', event.target.value)}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderProjectTariffPricingManager = () => {
    if (activeKey !== 'projects' || !selected?.id) return null;

    return (
      <div className="mt-8 space-y-6 border-t border-gray-200 pt-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Categorías Tarifarias y Precios</h3>
          <p className="text-sm text-gray-600">
            Configurá categorías propias de este parque. El simulador usa estos precios junto con las variables financieras del proyecto.
          </p>
        </div>

        {tariffLoading ? (
          <p className="text-sm text-gray-500">Cargando categorías tarifarias...</p>
        ) : (
          <div className="space-y-4">
            {tariffPrices.map((tariffPrice) => (
              <div key={tariffPrice.id} className="rounded-xl border border-gray-200 p-4">
                {renderTariffPriceFields(
                  tariffPrice,
                  (field, value) => handleTariffPriceChange(tariffPrice.id, field, value),
                  'existing-tariff'
                )}
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => handleTariffPriceDelete(tariffPrice.id)}
                    disabled={tariffSaving}
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                  >
                    Eliminar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTariffPriceSave(tariffPrice)}
                    disabled={tariffSaving}
                    className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                  >
                    Guardar categoría
                  </button>
                </div>
              </div>
            ))}
            {tariffPrices.length === 0 && <p className="text-sm text-gray-500">Sin categorías configuradas.</p>}
          </div>
        )}

        <form onSubmit={handleTariffPriceCreate} className="rounded-xl border border-dashed border-gray-300 p-4">
          <h4 className="mb-4 font-semibold text-gray-900">Agregar nueva categoría</h4>
          {renderTariffPriceFields(tariffForm, handleTariffFormChange, 'new-tariff')}
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={tariffSaving}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              Agregar categoría
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderProjectMediaManager = () => {
    if (activeKey !== 'projects' || !selected?.id) return null;

    return (
      <div className="mt-8 space-y-6 border-t border-gray-200 pt-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Logos y multimedia</h3>
          <p className="text-sm text-gray-600">
            Estos archivos alimentan las tarjetas, el encabezado del detalle y la galería del proyecto.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {[
            { type: 'logo', label: 'Logo del proyecto', url: selected.logo_url, imageClass: 'max-h-16 w-auto' },
            { type: 'name_logo', label: 'Nombre en imagen/SVG', url: selected.name_logo_url, imageClass: 'max-h-16 w-auto' },
            {
              type: 'banner',
              label: 'Banner',
              url: selected.banner_url,
              helpText: 'Dimensión recomendada: 400px alto x 1200px ancho (relación 3:1).',
              imageClass: 'h-28 w-full rounded object-cover',
              wide: true,
            },
          ].map((item) => (
            <div key={item.type} className={`rounded-xl border border-gray-200 p-4 ${item.wide ? 'md:col-span-2' : ''}`}>
              <label className="mb-2 block text-sm font-medium text-gray-700">{item.label}</label>
              {item.helpText && <p className="mb-3 text-xs text-gray-500">{item.helpText}</p>}
              {item.url ? (
                <div className="mb-3 rounded-lg bg-gray-50 p-3">
                  <img src={item.url} alt={item.label} className={item.imageClass} />
                </div>
              ) : (
                <p className="mb-3 text-sm text-gray-500">Sin archivo cargado.</p>
              )}
              <input
                type="file"
                accept="image/*,.svg"
                disabled={logoSaving}
                onChange={(event) => handleLogoUpload(event, item.type)}
                className="mb-3 block w-full text-sm text-gray-700"
              />
              {item.url && (
                <button
                  type="button"
                  onClick={() => handleLogoDelete(item.type)}
                  disabled={logoSaving}
                  className="rounded-lg border border-red-300 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  Eliminar
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 p-4">
            <h4 className="mb-3 font-semibold text-gray-900">Fotos</h4>
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              {(selected.images || []).map((image) => (
                <div key={image.id} className="rounded-lg border border-gray-200 p-2">
                  <img src={image.image_url} alt={image.caption || selected.name} className="mb-2 h-28 w-full rounded object-cover" />
                  <p className="text-xs text-gray-600">{image.caption || 'Sin descripción'}</p>
                  {image.is_featured && <p className="text-xs font-semibold text-primary-600">Imagen principal</p>}
                  <button
                    type="button"
                    onClick={() => handleMediaDelete('image', image.id)}
                    disabled={imageSaving}
                    className="mt-2 text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                  >
                    Eliminar
                  </button>
                </div>
              ))}
            </div>
            <form onSubmit={handleImageSubmit} className="space-y-3">
              <input name="image" type="file" accept="image/*" className="block w-full text-sm" />
              <input name="caption" placeholder="Descripción" className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              <div className="grid grid-cols-2 gap-3">
                <input name="order" type="number" defaultValue="0" placeholder="Orden" className="rounded-lg border border-gray-300 px-3 py-2" />
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input name="is_featured" type="checkbox" value="true" />
                  Principal
                </label>
              </div>
              <button type="submit" disabled={imageSaving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
                Agregar foto
              </button>
            </form>
          </div>

          <div className="rounded-xl border border-gray-200 p-4">
            <h4 className="mb-3 font-semibold text-gray-900">Videos</h4>
            <div className="mb-4 space-y-3">
              {(selected.videos || []).map((video) => (
                <div key={video.id} className="rounded-lg border border-gray-200 p-3">
                  <p className="font-medium text-gray-900">{video.title}</p>
                  <p className="text-xs text-gray-600">{video.video_file_url || video.video_url}</p>
                  <button
                    type="button"
                    onClick={() => handleMediaDelete('video', video.id)}
                    disabled={videoSaving}
                    className="mt-2 text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                  >
                    Eliminar
                  </button>
                </div>
              ))}
            </div>
            <form onSubmit={handleVideoSubmit} className="space-y-3">
              <input name="title" placeholder="Título" required className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              <input name="video" type="file" accept="video/*" className="block w-full text-sm" />
              <input name="video_url" type="url" placeholder="URL de YouTube/Vimeo opcional" className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              <textarea name="description" placeholder="Descripción" rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              <input name="order" type="number" defaultValue="0" placeholder="Orden" className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              <button type="submit" disabled={videoSaving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
                Agregar video
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderField = (field) => {
    const baseClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100';
    const value = form?.[field.name] ?? '';
    const displayValue = field.mwhInputForKwhField
      ? mwhInputDrafts[field.name] ?? formatLocalizedNumber(kwhToMwhValue(value))
      : field.localizedNumber
        ? formatLocalizedNumber(value)
        : value;

    if (field.type === 'checkbox') {
      return (
        <label className="flex items-center space-x-3 rounded-lg border border-gray-200 p-3">
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={field.readOnly}
            onChange={(event) => handleFieldChange(field, event.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600"
          />
          <span className="font-medium text-gray-700">{field.label}</span>
        </label>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          className={baseClass}
          rows={4}
          value={value}
          disabled={field.readOnly}
          required={field.required}
          onChange={(event) => handleFieldChange(field, event.target.value)}
        />
      );
    }

    if (field.type === 'select') {
      return (
        <select
          className={baseClass}
          value={value}
          disabled={field.readOnly}
          required={field.required}
          onChange={(event) => handleFieldChange(field, event.target.value)}
        >
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'projectUrl') {
      const slugValue = value || '';
      return (
        <div className="space-y-2">
          <div className="flex overflow-hidden rounded-lg border border-gray-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
            <span className="hidden items-center bg-gray-50 px-3 text-sm text-gray-500 md:flex">
              {PUBLIC_PROJECT_URL_BASE}
            </span>
            <input
              className="min-w-0 flex-1 px-3 py-2 text-sm focus:outline-none"
              type="text"
              value={slugValue}
              disabled={field.readOnly}
              placeholder="se-genera-automaticamente"
              onChange={(event) => handleFieldChange(field, event.target.value)}
            />
          </div>
          <p className="break-all text-xs text-gray-500">
            URL final: {PUBLIC_PROJECT_URL_BASE}{slugValue || 'se-genera-automaticamente'}
          </p>
        </div>
      );
    }

    if (field.mwhInputForKwhField) {
      return (
        <div className="grid items-end gap-3 sm:grid-cols-2">
          <input
            className={baseClass}
            type="text"
            inputMode="decimal"
            value={displayValue}
            disabled={field.readOnly}
            required={field.required}
            onChange={(event) => handleFieldChange(field, event.target.value)}
            onBlur={() => handleMwhFieldBlur(field)}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Equivalente en kWh/año</label>
            <input
              className={`${baseClass} bg-gray-50 text-gray-600`}
              value={formatLocalizedNumber(value)}
              readOnly
            />
          </div>
        </div>
      );
    }

    if (field.localizedNumber) {
      const numberInput = (
        <input
          className={field.suffix ? 'min-w-0 flex-1 border-0 px-3 py-2 text-sm focus:outline-none' : baseClass}
          type="text"
          inputMode="decimal"
          value={displayValue}
          disabled={field.readOnly}
          required={field.required}
          onChange={(event) => handleFieldChange(field, event.target.value)}
        />
      );

      if (!field.suffix) {
        return numberInput;
      }

      return (
        <div className="flex overflow-hidden rounded-lg border border-gray-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
          {numberInput}
          <span className="flex items-center bg-gray-50 px-3 text-sm font-medium text-gray-600">
            {field.suffix}
          </span>
        </div>
      );
    }

    return (
      <input
        className={baseClass}
        type={field.type || 'text'}
        step={field.type === 'number' ? '0.01' : undefined}
        value={displayValue}
        disabled={field.readOnly}
        required={field.required}
        onChange={(event) => handleFieldChange(field, event.target.value)}
      />
    );
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-soft"
        >
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Administración</h1>
          <p className="mb-6 text-sm text-gray-600">
            Ingresá con un usuario de Django que sea staff o superuser.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Usuario</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                value={loginForm.username}
                onChange={(event) => setLoginForm({ ...loginForm, username: event.target.value })}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Contraseña</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                type="password"
                value={loginForm.password}
                onChange={(event) => setLoginForm({ ...loginForm, password: event.target.value })}
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col justify-between gap-4 rounded-2xl bg-white p-6 shadow-soft md:flex-row md:items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Administración</h1>
            <p className="text-sm text-gray-600">Sesión activa: {user?.username || 'admin'}</p>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
          >
            Cerrar sesión
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="rounded-2xl bg-white p-4 shadow-soft">
            <div className="space-y-2">
              {Object.entries(resourceConfigs).map(([key, item]) => (
                <button
                  key={key}
                  onClick={() => setActiveKey(key)}
                  className={`w-full rounded-lg px-4 py-3 text-left text-sm font-medium transition ${
                    activeKey === key
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </aside>

          <section className="grid gap-6 xl:grid-cols-[240px_minmax(0,1fr)]">
            <div className="rounded-2xl bg-white p-4 shadow-soft">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">{config.title}</h2>
                {!config.singleton && !config.readOnlyCreate && (
                  <button
                    onClick={handleNew}
                    className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                  >
                    Nuevo
                  </button>
                )}
              </div>

              {loading ? (
                <p className="text-sm text-gray-500">Cargando...</p>
              ) : (
                <div className="max-h-[640px] space-y-2 overflow-y-auto">
                  {items.map((item) => (
                    <button
                      key={item.id || 'singleton'}
                      onClick={() => handleSelect(item)}
                      className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${
                        selected?.id === item.id || config.singleton
                          ? 'border-primary-200 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {config.label(item)}
                    </button>
                  ))}
                  {items.length === 0 && <p className="text-sm text-gray-500">Sin registros.</p>}
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-soft">
              <form onSubmit={handleSave} className="mb-6">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selected?.id || config.singleton ? 'Editar' : 'Nuevo registro'}
                  </h2>
                  {!config.singleton && selected?.id && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={saving}
                      className="rounded-lg border border-red-300 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                    >
                      Eliminar
                    </button>
                  )}
                </div>

                {activeKey === 'projects' && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Características Principales</h3>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  {config.fields
                    .filter((field) => !field.showWhen || field.showWhen(form))
                    .map((field) => (
                      <div key={field.name} className={field.type === 'textarea' || field.wide ? 'md:col-span-2' : ''}>
                        {field.type !== 'checkbox' && (
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            {field.label}
                          </label>
                        )}
                        {renderField(field)}
                        {field.helpText && (
                          <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                        )}
                      </div>
                    ))}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-primary-600 px-5 py-2 font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                  >
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </form>
              {renderProjectSalePricingManager()}
              {renderParticipationSalesManager()}
              {renderProjectTariffPricingManager()}
              {renderProjectMediaManager()}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdministrationPage;
